import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Smartphone, 
  Download, 
  Zap, 
  Wifi, 
  Bell,
  Share,
  PlusSquare,
  MoreVertical,
  Chrome
} from "lucide-react";
import { motion } from "framer-motion";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export default function Install() {
  const [platform, setPlatform] = useState<"ios" | "android" | "desktop">("desktop");
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Detect platform
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setPlatform("ios");
    } else if (/android/.test(userAgent)) {
      setPlatform("android");
    }

    // Check if already installed
    const standalone = window.matchMedia("(display-mode: standalone)").matches
      || (window.navigator as any).standalone === true;
    setIsInstalled(standalone);

    // Listen for install prompt
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      setDeferredPrompt(null);
    }
  };

  const benefits = [
    {
      icon: Zap,
      title: "Lightning Fast",
      description: "Loads instantly from your home screen"
    },
    {
      icon: Wifi,
      title: "Works Offline",
      description: "Browse prompts even without internet"
    },
    {
      icon: Bell,
      title: "Stay Updated",
      description: "Never miss likes, comments, or saves"
    },
    {
      icon: Smartphone,
      title: "Native Feel",
      description: "Full-screen experience like a native app"
    }
  ];

  return (
    <Layout>
      <div className="container max-w-2xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/20 mb-6">
            <Smartphone className="h-10 w-10 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-3">Install ThePromptSpace</h1>
          <p className="text-muted-foreground">
            Get the full app experience on your device
          </p>
        </motion.div>

        {isInstalled ? (
          <Card className="mb-8">
            <CardContent className="p-6 text-center">
              <div className="text-green-500 mb-2">✓</div>
              <h2 className="font-semibold mb-2">Already Installed!</h2>
              <p className="text-sm text-muted-foreground">
                ThePromptSpace is installed on your device. Launch it from your home screen.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Benefits */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {benefits.map((benefit, i) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Card>
                    <CardContent className="p-4 text-center">
                      <benefit.icon className="h-6 w-6 text-primary mx-auto mb-2" />
                      <h3 className="font-medium text-sm mb-1">{benefit.title}</h3>
                      <p className="text-xs text-muted-foreground">{benefit.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Install Instructions */}
            <Card className="mb-8">
              <CardContent className="p-6">
                <h2 className="font-semibold text-lg mb-4 text-center">
                  How to Install
                </h2>

                {platform === "ios" && (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground text-center mb-4">
                      On your iPhone or iPad in Safari:
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold text-sm">
                          1
                        </div>
                        <div>
                          <p className="font-medium text-sm">Tap the Share button</p>
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <Share className="h-3 w-3" /> at the bottom of the screen
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold text-sm">
                          2
                        </div>
                        <div>
                          <p className="font-medium text-sm">Tap "Add to Home Screen"</p>
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <PlusSquare className="h-3 w-3" /> Scroll down to find it
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold text-sm">
                          3
                        </div>
                        <div>
                          <p className="font-medium text-sm">Tap "Add"</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Done! Find the app on your home screen
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {platform === "android" && (
                  <div className="space-y-4">
                    {deferredPrompt ? (
                      <div className="text-center">
                        <Button onClick={handleInstall} size="lg" className="w-full">
                          <Download className="h-5 w-5 mr-2" />
                          Install Now
                        </Button>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-muted-foreground text-center mb-4">
                          On your Android device in Chrome:
                        </p>
                        <div className="space-y-4">
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold text-sm">
                              1
                            </div>
                            <div>
                              <p className="font-medium text-sm">Tap the menu button</p>
                              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                <MoreVertical className="h-3 w-3" /> Three dots in the top right
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold text-sm">
                              2
                            </div>
                            <div>
                              <p className="font-medium text-sm">Tap "Install app" or "Add to Home screen"</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold text-sm">
                              3
                            </div>
                            <div>
                              <p className="font-medium text-sm">Tap "Install"</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Done! Find the app on your home screen
                              </p>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {platform === "desktop" && (
                  <div className="space-y-4">
                    {deferredPrompt ? (
                      <div className="text-center">
                        <Button onClick={handleInstall} size="lg">
                          <Download className="h-5 w-5 mr-2" />
                          Install Now
                        </Button>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-muted-foreground text-center mb-4">
                          In Chrome or Edge:
                        </p>
                        <div className="space-y-4">
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold text-sm">
                              1
                            </div>
                            <div>
                              <p className="font-medium text-sm">Look for the install icon</p>
                              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                <Chrome className="h-3 w-3" /> In the address bar on the right
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold text-sm">
                              2
                            </div>
                            <div>
                              <p className="font-medium text-sm">Click "Install"</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                The app will open in its own window
                              </p>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
